import {getTasksInCardSelector} from 'stores/selectors/cardStateSelector';
import {useDispatch, useSelector} from 'react-redux';

import {
  SerialDilutionStep,
  ProtocolEditorViewMode,
  TaskType,
  CardType,
  RuntimeMode,
  ExecutionStatus,
} from 'constants/dataEnum';
import {useArrowDownPress, useArrowUpPress} from 'lib/customHooks';

import React, {useMemo, useRef, useCallback} from 'react';
import _, {orderBy} from 'lodash';

import PropTypes from 'prop-types';
import jsonCopy from 'lib/jsonCopy';
import {filterToArray, filter} from 'lib/objectHelper';

import {mapToSerialDilutionStep} from 'lib/serialDilutionHelper';
import {
  generateSdCompleteTasks,
  getSdTaskExecutionStatus,
  saveSdTemporaryStates,
} from 'stores/business/taskListSerialDilutionBusiness';
import {initiateGlobalWorker} from 'workers/initiateWorker';
import {setSelectedMenu, setSerialDilutionEditStep, setSerialDilutionStep} from 'stores/actions/protocolEditorAction';
import {globalAddUndoItem} from 'stores/business/undoRedoBusinessAction';
import {mergeRefs} from 'lib/refHelper';
import {hideLoading, showLoading} from 'stores/actions/appStateAction';
import {
  getProtocolEditorMode,
  getSerialDilutionEditStep,
  getSerialDilutionStep,
} from 'stores/selectors/protocolEditorSelectors';

import {setCardSdProperty} from 'stores/actions/cardSerialDilutionStateAction';
import {showReuseTipsModal} from 'stores/business/modalStateBusinessAction';

import {getCardExecutionStatus} from 'stores/business/runtimeBusiness';
import {getRuntimeStatus} from 'stores/selectors/runtimeSelector';

import TaskDilutionSource from './Tasks/TaskDilutionSource';
import TaskTransferAliquot from './Tasks/TaskTransferAliquot';
import TaskDilutionTransfer from './Tasks/TaskDilutionTransfer';

const SerialDilutionTasksContainer = React.forwardRef(
  (
    {
      card,

      onSelectTask,
      runtimeMode,
      readOnly,

      onArrowDownPress,
      onArrowUpPress,

      highlightedTask,
    },
    ref,
  ) => {
    const dispatch = useDispatch();

    const serialDilutionStep = useSelector(getSerialDilutionStep);
    const serialDilutionEditStep = useSelector(getSerialDilutionEditStep);
    const protocolEditorMode = useSelector(getProtocolEditorMode);

    const runtimeStatus = useSelector(getRuntimeStatus);
    const executionStatus = dispatch(getCardExecutionStatus(card));

    const processedTasks = useSelector(getTasksInCardSelector(card));
    const orderedProcessedTasks = useMemo(() => orderBy(processedTasks, ['number'], ['asc']), [processedTasks]);

    const {sdRawTasks, sdRawTasksSummary, sdProperty} = card;
    const {sampleVolume, aliquotVolume, stepProperties, diluentVolume, step} = jsonCopy(sdProperty);

    const {
      taskSampleSource: taskSampleSourceId,
      taskDiluentSource: taskDiluentSourceId,
      taskSampleTarget: taskSampleTargetIds,
      taskDiluentTargets: taskDiluentTargetIds,
    } = sdRawTasksSummary;

    const serialDilutionContainerRef = useRef(null);

    useArrowUpPress(serialDilutionContainerRef, onArrowUpPress);
    useArrowDownPress(serialDilutionContainerRef, onArrowDownPress);

    const adjustDisplayedVolume = useCallback(
      (rawTask) => {
        if (!rawTask) return {...rawTask, volume: 0};

        // TODO: Need to store the default fixed precision for the transfer volume in a config state or somewhere
        let totalDiluent = stepProperties
          ?.filter((e) => e.step <= step)
          // eslint-disable-next-line no-unsafe-optional-chaining
          .reduce((acc, val) => acc + val?.stepDiluentVolume || 0, 0);
        totalDiluent = Number((totalDiluent || 0).toFixed(1));

        switch (rawTask?.number) {
          case SerialDilutionStep.SET_DILUENT_SOURCE:
            return {...rawTask, volume: totalDiluent ?? diluentVolume};
          case SerialDilutionStep.SET_SAMPLE_SOURCE:
            return {...rawTask, volume: sampleVolume};
          case SerialDilutionStep.SET_SAMPLE_TARGET:
            return {...rawTask, volume: aliquotVolume ?? sampleVolume};
          case SerialDilutionStep.SET_DILUENT_TARGET: {
            return {...rawTask, volume: totalDiluent ?? diluentVolume};
          }
          default:
            return {...rawTask, volume: 0};
        }
      },
      [aliquotVolume, diluentVolume, sampleVolume, step, stepProperties],
    );

    const sortedProcessTasksByNumberAndType = useMemo(() => {
      const sortedTask = {};

      for (let i = 0; i < orderedProcessedTasks.length; i += 1) {
        const task = orderedProcessedTasks[i];
        const {number, type, skipped} = task || {};

        const taskInSameNumber = sortedTask[number];
        const taskInSameNumberSameType = taskInSameNumber ? taskInSameNumber[type] || [] : [];

        if (type !== TaskType.ASPIRATE && type !== TaskType.DISPENSE) continue;
        if (skipped) continue;

        sortedTask[number] = {
          ...taskInSameNumber,
          [type]: [...taskInSameNumberSameType, {...task}],
        };
      }

      return sortedTask;
    }, [orderedProcessedTasks]);

    const shownTaskDetail = useMemo(() => {
      const taskDiluentSource = _.isNumber(taskDiluentSourceId)
        ? adjustDisplayedVolume(sdRawTasks[taskDiluentSourceId])
        : null;
      const taskDiluentTargets = taskDiluentTargetIds?.length
        ? filterToArray(sdRawTasks, {ID: taskDiluentTargetIds})
        : null;

      const taskSampleSource = _.isNumber(taskSampleSourceId)
        ? adjustDisplayedVolume(sdRawTasks[taskSampleSourceId])
        : null;

      let taskSampleTarget = null;

      // sample target ids can either be stored as a number (1D swiping) or array (2D swiping)
      if (_.isNumber(taskSampleTargetIds)) {
        taskSampleTarget = adjustDisplayedVolume(sdRawTasks[taskSampleTargetIds]);
      } else if (taskSampleTargetIds.length) {
        taskSampleTarget = taskSampleTargetIds?.length ? filterToArray(sdRawTasks, {ID: taskSampleTargetIds}) : null;

        taskSampleTarget[0] = adjustDisplayedVolume(taskSampleTarget[0]);
      }

      const mappedTask = mapToSerialDilutionStep(
        taskDiluentSource,
        taskSampleSource,
        taskSampleTarget,
        taskDiluentTargets,
      );

      return filter(mappedTask, (task) => task);
    }, [
      adjustDisplayedVolume,
      sdRawTasks,
      taskDiluentSourceId,
      taskDiluentTargetIds,
      taskSampleSourceId,
      taskSampleTargetIds,
    ]);

    const onReuseTipsHandler = (e) => {
      const reuseTipsHandler = async (isChecked) => {
        await dispatch(
          setCardSdProperty(card?.ID, {
            ...sdProperty,
            reuseTips: isChecked,
          }),
        );
        await dispatch(generateSdCompleteTasks(card?.ID));
        dispatch(globalAddUndoItem());
      };

      if (!sdProperty?.reuseTips) {
        dispatch(showReuseTipsModal(() => reuseTipsHandler(e)));
      } else {
        reuseTipsHandler(e);
      }
    };

    const doSelectTask = (taskNumber) => {
      const selectedTasksNumber = shownTaskDetail[taskNumber];
      const selectedTasks = _.isArray(selectedTasksNumber) ? selectedTasksNumber : [selectedTasksNumber];

      onSelectTask({
        taskId: selectedTasks[0].ID,
        taskNumber,
        cardType: CardType.SERIAL_DILUTION,
        taskList: selectedTasks,
        cardID: card.ID,
      });
    };

    async function onEdit(sdStep) {
      // unselect all tasks first
      dispatch(showLoading());
      initiateGlobalWorker(async () => {
        onSelectTask();
        const editStepToShow =
          sdStep !== SerialDilutionStep.SET_DILUENT_TARGET ? sdStep : SerialDilutionStep.SERIAL_DILUTION_SETTINGS;

        await dispatch(setSerialDilutionStep(editStepToShow));
        await dispatch(setSerialDilutionEditStep(editStepToShow));
        await dispatch(saveSdTemporaryStates(card.ID));
        await dispatch(setSelectedMenu(ProtocolEditorViewMode.SERIAL_DILUTION));
        dispatch(globalAddUndoItem());
        dispatch(hideLoading());
      });
    }

    const isEditDisabled = (sdStep) => {
      if (runtimeMode) {
        return true;
      }

      if (serialDilutionEditStep) {
        return true;
      }

      if (protocolEditorMode !== ProtocolEditorViewMode.SERIAL_DILUTION) {
        return false;
      }

      return sdStep === serialDilutionStep;
    };

    const getTaskProps = (taskNumber, index) => ({
      processedTasks: sortedProcessTasksByNumberAndType[taskNumber],
      executionStatus: dispatch(getSdTaskExecutionStatus(taskNumber, card)),
      onSelectTask: () => doSelectTask(taskNumber),
      isSelected: highlightedTask?.ID === taskNumber,
      readOnly: isEditDisabled(taskNumber) || readOnly,
      onEdit: () => onEdit(taskNumber),
      runtimeMode,
      showPipettingParameter: isShowPipettingProfile,
      number: index + 1,
    });

    const isShowPipettingProfile =
      !readOnly &&
      ((runtimeMode && runtimeStatus === RuntimeMode.PAUSED && executionStatus === ExecutionStatus.NONE) ||
        !runtimeMode);

    return (
      <div
        className="serial-dilution-tasks-container"
        ref={mergeRefs(serialDilutionContainerRef, ref)}
        style={{width: '100%'}}
      >
        {Object.keys(shownTaskDetail)
          .map((strKey) => Number(strKey))
          .map((taskNumber, index) => (
            <>
              {(taskNumber === SerialDilutionStep.SET_DILUENT_SOURCE ||
                taskNumber === SerialDilutionStep.SET_SAMPLE_SOURCE) && (
                // eslint-disable-next-line react/jsx-props-no-spreading
                <TaskDilutionSource task={shownTaskDetail[taskNumber]} {...getTaskProps(taskNumber, index)} />
              )}
              {taskNumber === SerialDilutionStep.SET_SAMPLE_TARGET && (
                // eslint-disable-next-line react/jsx-props-no-spreading
                <TaskTransferAliquot task={shownTaskDetail[taskNumber][0]} {...getTaskProps(taskNumber, index)} />
              )}
              {taskNumber === SerialDilutionStep.SET_DILUENT_TARGET && (
                <TaskDilutionTransfer
                  tasks={shownTaskDetail[taskNumber]}
                  sdProperty={sdProperty}
                  onReuseTipsChange={onReuseTipsHandler}
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...getTaskProps(taskNumber, index)}
                />
              )}
            </>
          ))}
      </div>
    );
  },
);

SerialDilutionTasksContainer.propTypes = {
  card: PropTypes.object.isRequired,

  onSelectTask: PropTypes.func.isRequired,
  onArrowDownPress: PropTypes.func.isRequired,
  onArrowUpPress: PropTypes.func.isRequired,

  runtimeMode: PropTypes.bool.isRequired,
  readOnly: PropTypes.bool.isRequired,

  highlightedTask: PropTypes.object.isRequired,
};

export default SerialDilutionTasksContainer;
